<template>
  <div class="yourUnitverse">
    <Loading v-if="isLoading" variant="page" message="Loading dashboard…" />
    <div v-else class="fullWidth">
      <section
        v-if="!hasActiveOrganisations"
        class="dashboard-organisation-zero-state"
        aria-labelledby="dashboard-organisation-zero-title"
      >
        <div>
          <h2 id="dashboard-organisation-zero-title">Start your dashboard</h2>
          <p>Create an organisation or use an invite link to join one.</p>
        </div>
        <div class="dashboard-organisation-zero-state__actions">
          <UnitButton variant="text" @click="openOrganisationCreation">
            Create organisation
          </UnitButton>
          <UnitButton variant="text" @click="openInvites">
            Use an invite link
          </UnitButton>
        </div>
      </section>

      <div v-else>
        <div
          v-for="(unitData, unitUID) in userUnits_customOrgs"
          :key="unitUID"
          class="userUnits"
        >
          <!-- DASHBOARD NAVIGATION -->
          <nav class="dashboard-nav" aria-label="Organisation dashboard">
            <UnitTabs
              :tabs="dashboardTabs"
              :model-value="pressedCard"
              label="Dashboard sections"
              id-prefix="dashboard"
              panel-id-prefix="dashboard-panel"
              @update:model-value="selectDashboardTab"
            />
            <div class="dashboard-org-actions">
              <OrgContextPicker
                v-if="hasMultipleOrgs"
                class="dashboard-org-context"
              />
              <UnitButton
                class="create-organisation-action"
                variant="text"
                size="small"
                @click="router.push({ name: 'organisation-new' })"
              >
                Create organisation
              </UnitButton>
            </div>
          </nav>
          <!-- OVERVIEW -->
          <div>
            <div
              v-show="isSectionVisible('overview')"
              id="dashboard-panel-overview"
              role="tabpanel"
              aria-labelledby="dashboard-tab-overview"
              class="card-cont-wrapper fade-in-out"
            >
              <div class="dashboardSection">
                <OrgOverview
                  v-if="unitData"
                  :unitData="unitData"
                  :orgUID="unitUID"
                  :userRolePrivileges="userRolePrivileges"
                  :backgroundColor="getColorForCard(selectedIndex)"
                />
                <OrgUsagePanel
                  v-if="
                    !isPrivateOrganisationContextMode &&
                    unitData &&
                    userRolePrivileges?.roleAdministration
                  "
                  :org-id="unitUID"
                  style="margin-top: var(--space-7)"
                />
                <ApiKeysPanel
                  v-if="
                    unitData &&
                    (!isPrivateOrganisationContextMode ||
                      semanticPermissions.canManageApiKeys)
                  "
                  :org-id="unitUID"
                  :actor-uid="userCredentials?.data?.uid || ''"
                  style="margin-top: var(--space-7)"
                />
              </div>
            </div>
          </div>

          <!-- MEMBERS -->
          <div>
            <div
              v-if="isSectionVisible('members')"
              id="dashboard-panel-members"
              role="tabpanel"
              aria-labelledby="dashboard-tab-members"
              class="card-cont-wrapper fade-in-out"
            >
              <div class="dashboardSection">
                <PrivateOrganisationAdministration
                  v-if="isPrivateOrganisationContextMode"
                  :org-id="unitUID"
                  section="members"
                  :organisation-public-identifier="
                    unitData?.publicIdentifier || ''
                  "
                  :background-color="getColorForCard(selectedIndex)"
                />
              </div>
            </div>
          </div>
          <!-- ROLES -->
          <div>
            <div
              v-if="
                isSectionVisible('roles') &&
                (isPrivateOrganisationContextMode
                  ? semanticPermissions.canManageRoles
                  : userRolePrivileges?.roleAdministration ||
                    userRolePrivileges?.roleCreation)
              "
              id="dashboard-panel-roles"
              role="tabpanel"
              aria-labelledby="dashboard-tab-roles"
              class="card-cont-wrapper fade-in-out"
            >
              <div class="dashboardSection">
                <PrivateOrganisationAdministration
                  v-if="isPrivateOrganisationContextMode"
                  :org-id="unitUID"
                  section="roles"
                  :background-color="getColorForCard(selectedIndex)"
                />
              </div>
            </div>
          </div>
          <!-- MONETARY PARAMS -->
          <div
            v-show="
              !isPrivateOrganisationContextMode &&
              isSectionVisible('monetary') &&
              userRolePrivileges?.monetaryParameters
            "
            id="dashboard-panel-monetary"
            role="tabpanel"
            aria-labelledby="dashboard-tab-monetary"
            class="card-cont-wrapper fade-in-out"
          >
            <div class="dashboardSection">
              <MonetaryParametersPanel
                v-if="
                  !isPrivateOrganisationContextMode &&
                  unitData &&
                  unitData.monetary_parameters?.current_account
                "
                :unitData="unitData"
                :orgUID="unitUID"
                :monetaryParameters="unitData.monetary_parameters"
                :backgroundColor="getColorForCard(selectedIndex)"
                :userRolePrivileges="userRolePrivileges"
                :getParameterDisplayString="getParameterDisplayString"
              />
            </div>
          </div>
          <div class="column">
            <router-link
              v-if="
                !blocksLegacyOrganisationRoute || unitData.publicIdentifier
              "
              :to="{
                name: 'OrganisationView',
                params: {
                  orgUID: blocksLegacyOrganisationRoute
                    ? unitData.publicIdentifier
                    : unitUID,
                },
              }"
              @click="resetActiveSpace"
            >
              <UnitButton variant="secondary" size="small" v-haptic>
                <template #leading>
                  <svg class="compactImg" aria-hidden="true">
                    <use href="../../assets/icons/iconset.svg#view_compact"></use>
                  </svg>
                </template>
                <span>View <strong>{{ unitData.name }}</strong> public page</span>
              </UnitButton>
            </router-link>
          </div>
        </div>
        <!-- end of loop through user units -->
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from "vuex";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";

import {
  computed,
  onMounted,
  watch,
  reactive,
  ref,
} from "vue";
import { toast } from "vue3-toastify";
// import OrgTemplate from "@/components/OrgComponents/OrgTemplate/OrgTemplate.vue";

// import CreateOrgTemplate from "@/components/OrgComponents/OrgTemplate/CreateOrgTemplate.vue";
import PrivateOrganisationAdministration from "@/components/DashboardComponents/PrivateOrganisationAdministration.vue";
import OrgOverview from "@/components/DashboardComponents/OrgOverview.vue";
import UnitTabs from "@/components/GenericComponents/UnitTabs.vue";
import OrgUsagePanel from "@/components/DashboardComponents/OrgUsagePanel.vue";
import ApiKeysPanel from "@/components/DashboardComponents/ApiKeysPanel.vue";
import Loading from "@/components/GenericComponents/LoadingState.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import OrgContextPicker from "@/components/OrgComponents/OrgContextPicker.vue";
import MonetaryParametersPanel from "@/components/OrgComponents/OrgCustom/MonetaryParametersPanel.vue";
import {
  isItemAuthorityReadsFeatureEnabled,
  isOrganisationAuthorityReadsFeatureEnabled,
  isOrgContextFeatureEnabled,
  isOrgPermissionReadsFeatureEnabled,
} from "@/config/runtimeConfig";
// import CardButton from "@/components/GenericComponents/CardButton.vue";

export default {
  name: "YourUnitverse",
  components: {
    // OrgTemplate,
    // CreateOrgTemplate,
    Loading,
    UnitButton,
    OrgContextPicker,
    MonetaryParametersPanel,
    PrivateOrganisationAdministration,
    OrgOverview,
    UnitTabs,
    OrgUsagePanel,
    ApiKeysPanel,
    // CardButton,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const isPrivateOrganisationContextMode =
      isOrgPermissionReadsFeatureEnabled;
    const blocksLegacyOrganisationRoute =
      isOrgContextFeatureEnabled ||
      isOrgPermissionReadsFeatureEnabled ||
      isOrganisationAuthorityReadsFeatureEnabled ||
      isItemAuthorityReadsFeatureEnabled;
    // monitoring changes to user org to display a diffreten button if user has selected fundation
    const selectedOrg = ref(null); // or reactive({}); based on your needs
    const handleSelectedOrg = (org) => {
      selectedOrg.value = org;
      // other logic can go here
    };

    const userRolePrivileges = computed(() => {
      if (isPrivateOrganisationContextMode) return {};
      return store.getters.userRolePrivileges
        ? store.getters.userRolePrivileges
        : {};
    });
    const semanticPermissions = computed(() => {
      if (!isPrivateOrganisationContextMode) return {};
      return store.getters.selectedOrganisationPermissions || {};
    });

    const sectionIndexes = {
      overview: 0,
      members: 1,
      roles: 2,
      monetary: 3,
    };
    const requestedInitialSection = Object.prototype.hasOwnProperty.call(
      sectionIndexes,
      route.params.section,
    )
      ? route.params.section
      : "overview";
    const initialSection = requestedInitialSection;
    const selectedIndex = ref(sectionIndexes[initialSection]);

    const userUnits_customOrgs = computed(() => {
      return store.getters.selectedCustomOrgProjection;
    });
    const canViewMonetaryParameters = computed(() => {
      return userRolePrivileges.value.monetaryParameters;
    });

    const sections = reactive([
      {
        key: "overview",
        title: "Overview",
        illustration: "your_default_illustration_path",
        iconId: "organisation_hierachical",
        visible: true,
      },
      {
        key: "members",
        title: "Members",
        illustration: "your_default_illustration_path",
        iconId: "members",
        visible: false,
      },
      {
        key: "roles",
        title: "Roles",
        illustration: "your_default_illustration_path",
        iconId: "roles",
        visible: false,
      },
      {
        key: "monetary",
        title: "Monetary parameters",
        illustration: "your_default_illustration_path",
        iconId: "monetary_params2",
        visible: false,
        canView: canViewMonetaryParameters,
      },
    ]);

    const resetActiveSpace = () => {
      store.dispatch("changeActiveTab", null);
    };
    const currentOrgUID = computed(() => store.getters.selectedOrgUID);
    const hasMultipleOrgs = computed(() => store.getters.hasMultipleOrgs);
    const hasActiveOrganisations = computed(
      () => (store.getters.userOrgIds || []).length > 0,
    );
    const openOrganisationCreation = () =>
      router.push({ name: "organisation-new" });
    const openInvites = () => router.push({ name: "invites" });

    const isLoading = computed(() => store.getters.isLoading);
    const pressedCard = ref(initialSection);

    const user = computed(() => {
      return store.getters.user;
    });
    const userCredentials = computed(() => {
      return store.getters.userCredentials;
    });

    const isOrgUIDLoading = ref(true); // Dedicated loading state for organization UID

    const fetchAndSetCurrentOrgUID = async () => {
      isOrgUIDLoading.value = true; // Set the loading state to true before fetching
      const userUID = userCredentials.value?.data?.uid;
      if (userUID) await store.dispatch("ensureSelectedOrg", { userUID });
      isOrgUIDLoading.value = false; // Reset the loading state once the fetch is complete
    };

    watch(isOrgUIDLoading, (newVal) => {
      if (!newVal) {
        // Proceed only if the loading state is false (i.e., loading is complete)
        if (currentOrgUID.value) {
          // toast.success(
          //   `Your Unitverse watch isOrgUIDLoading Current organization UID: ${currentOrgUID.value}`
          // );
        } else {
          toast.error("You aren't in any Unit at the moment.");
        }
      }
    });

    const DASHBOARD_TAB_INDEX = Object.freeze({
      overview: 0,
      members: 1,
      roles: 2,
      monetary: 3,
    });
    // Same visibility rules the card buttons used, now as one tab list.
    const dashboardTabs = computed(() => {
      const tabs = [{ id: "overview", label: "Overview" }];
      const permissions = semanticPermissions.value;
      const privileges = userRolePrivileges.value;
      if (
        !isPrivateOrganisationContextMode ||
        permissions.canManageMembers ||
        permissions.canManageRoles
      ) {
        tabs.push({ id: "members", label: "Members" });
      }
      if (
        isPrivateOrganisationContextMode
          ? permissions.canManageRoles
          : privileges?.roleAdministration || privileges?.roleCreation
      ) {
        tabs.push({ id: "roles", label: "Roles" });
      }
      if (!isPrivateOrganisationContextMode && privileges?.monetaryParameters) {
        tabs.push({ id: "monetary", label: "Monetary rules" });
      }
      return tabs;
    });
    const selectDashboardTab = (key) =>
      setPressedCard(key, DASHBOARD_TAB_INDEX[key]);

    const setPressedCard = (key, index) => {
      if (isPrivateOrganisationContextMode) {
        const permitted =
          key === "overview" ||
          (key === "members" &&
            (semanticPermissions.value.canManageMembers ||
              semanticPermissions.value.canManageRoles)) ||
          (key === "roles" && semanticPermissions.value.canManageRoles);
        if (!permitted) return;
      }
      pressedCard.value = key;
      selectedIndex.value = index;
      localStorage.setItem("lastPressedCard", key);
      localStorage.setItem("lastSelectedIndex", index.toString());
      if (route.name === "dashboard" && route.params.section !== key) {
        router.push({
          name: "dashboard",
          params: { section: key },
          query: route.query,
        });
      }
    };

    watch(
      () => route.params.section,
      (section) => {
        if (!Object.prototype.hasOwnProperty.call(sectionIndexes, section)) return;
        if (
          isPrivateOrganisationContextMode &&
          section === "monetary"
        ) {
          pressedCard.value = "overview";
          selectedIndex.value = sectionIndexes.overview;
          router.replace({
            name: "dashboard",
            params: { section: "overview" },
            query: route.query,
          });
          return;
        }
        pressedCard.value = section;
        selectedIndex.value = sectionIndexes[section];
        localStorage.setItem("lastPressedCard", section);
        localStorage.setItem(
          "lastSelectedIndex",
          sectionIndexes[section].toString(),
        );
      },
      { immediate: true },
    );

    const ensureAccessibleSection = () => {
      const section = pressedCard.value;
      const canViewMembers = isPrivateOrganisationContextMode
        ? semanticPermissions.value.canManageMembers ||
          semanticPermissions.value.canManageRoles
        : true;
      const canViewRoles = isPrivateOrganisationContextMode
        ? semanticPermissions.value.canManageRoles
        : userRolePrivileges.value?.roleAdministration ||
          userRolePrivileges.value?.roleCreation;
      const canViewMonetary = userRolePrivileges.value?.monetaryParameters;
      if (
        (section === "members" && !canViewMembers) ||
        (section === "roles" && !canViewRoles) ||
        (section === "monetary" &&
          (isPrivateOrganisationContextMode || !canViewMonetary))
      ) {
        router.replace({
          name: "dashboard",
          params: { section: "overview" },
          query: route.query,
        });
      }
    };
    watch(userRolePrivileges, (privileges) => {
      if (privileges) ensureAccessibleSection();
    });
    watch(semanticPermissions, ensureAccessibleSection);
    const getColorForCard = (index) => {
      const sectionKeys = ["overview", "members", "roles", "monetary"];
      const key = sectionKeys[index];
      if (pressedCard.value === key) {
        const colorList = [
          "var(--color-category-surface-1)",
          "var(--color-category-surface-2)",
          "var(--color-category-surface-3)",
          "var(--color-category-surface-4)",
        ];
        return colorList[index % colorList.length];
      }
      return "transparent"; // Default background color when not pressed
    };
    // define the data for the card buttons and a variable to keep track of which section is visible
    const toggleSectionVisibility = (key) => {
      // If the pressed card is the same as the last one, reset it
      if (pressedCard.value === key) {
        pressedCard.value = null;
      } else {
        pressedCard.value = key; // Only this card should be visible
      }
    };
    const isSectionVisible = (key) => {
      return sections.some((s) => s.key === key && s.key === pressedCard.value);
    };
    // Formating monetary parameters
    const parameterMap = reactive({
      basic_income: "Basic income",
      savings_account: { cap: "Savings acct. cap" },
      current_account: {
        cap: "Current acct. cap",
        lifespan: "Current acct. currency lifespan",
      },
      max_shared_pool: { cap: "Shared pool cap" },
    });
    const getParameterDisplayString = (parameter, subParameter) => {
      if (subParameter) {
        // Handle sub-parameters
        return parameterMap[parameter][subParameter];
      } else {
        // Handle main parameters
        return parameterMap[parameter];
      }
    };
    onMounted(async () => {
      store.commit("SET_LOADING_STATE", true); // Begin loading state

      try {
        const currentUserUID = userCredentials.value?.data?.uid;
        if (!currentUserUID) throw new Error("No authenticated user found.");
        await fetchAndSetCurrentOrgUID();
        if (currentOrgUID.value) {
          await store.dispatch("fetchAndCommitSelectedCustomOrg", {
            userId: currentUserUID,
            orgUID: currentOrgUID.value,
          });
        }
        // Check if an organization UID is successfully retrieved and show a toast accordingly
        if (currentOrgUID.value) {
          // console.log(
          //   "Your Unitverse OnMounted Current organization UID:",
          //   currentOrgUID.value
          // );
        } else {
          // console.error("No organisation found for the current user.");
        }
      } catch (error) {
        // Log and show any errors that occurred during the process
        // console.error("An error occurred:", error);
      } finally {
        store.commit("SET_LOADING_STATE", false); // End loading state
      }
    });

    watch(currentOrgUID, async (orgUID, previousOrgUID) => {
      const userUID = userCredentials.value?.data?.uid;
      if (!userUID || !orgUID || orgUID === previousOrgUID) return;
      await store.dispatch("fetchAndCommitSelectedCustomOrg", {
        userId: userUID,
        orgUID,
      });
    });

    onBeforeRouteLeave((to, from, next) => {
      localStorage.setItem("lastPressedCard", pressedCard.value);
      localStorage.setItem("lastSelectedIndex", selectedIndex.value.toString());
      next();
    });

    return {
      router,
      user,
      userCredentials,
      userUnits_customOrgs,
      isLoading,
      getParameterDisplayString,
      sections,
      toggleSectionVisibility,
      pressedCard,
      setPressedCard,
      dashboardTabs,
      selectDashboardTab,
      getColorForCard,
      selectedIndex,
      isSectionVisible,
      selectedOrg,
      handleSelectedOrg,
      currentOrgUID,
      hasMultipleOrgs,
      hasActiveOrganisations,
      openOrganisationCreation,
      openInvites,
      fetchAndSetCurrentOrgUID,
      userRolePrivileges,
      semanticPermissions,
      isPrivateOrganisationContextMode,
      blocksLegacyOrganisationRoute,
      canViewMonetaryParameters,
      resetActiveSpace,
    };
  },
};
</script>

<style scoped>
.yourUnitverse {
  width: 100%;
  height: fit-content;
}

.dashboard-organisation-zero-state {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  padding: var(--space-5);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
}

.dashboard-organisation-zero-state h2,
.dashboard-organisation-zero-state p {
  margin: 0;
}

.dashboard-organisation-zero-state p {
  margin-top: var(--space-2);
  color: var(--color-text-tertiary);
}

.dashboard-organisation-zero-state__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-3);
}

.admin-action-container {
  margin-bottom: var(--space-5);
  padding: var(--space-4);
}

.noHeight {
  height: 0;
  border: 1px solid red;
}

.unitHeader {
  justify-content: space-between;
}

.list {
  width: 100%;
  margin-top: 0px;
  position: relative;
}
.list::before {
  content: "";
  position: absolute;
  top: calc(-1 * var(--space-4));
  left: 0;
  width: 100%;
  height: 3px;
  box-shadow: var(--shadow-divider-raised);
  filter: none;
}

/* DASHBOARD NAVIGATION: tabs and organisation actions share one row. */
.dashboard-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3) var(--space-5);
  margin-bottom: var(--space-5);
}

.dashboard-org-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2) var(--space-4);
  margin-inline-start: auto;
  min-width: 0;
}

.dashboard-org-context {
  min-width: 12rem;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.fade-in-out {
  animation: fadeIn 0.5s forwards;
  /* for fade out you'd swap to using the fadeOut keyframes */
}

/* 2 columns */
@media (max-width: 800px) {
  .dashboard-org-actions {
    margin-inline-start: 0;
  }

  .dashboard-organisation-zero-state {
    align-items: flex-start;
    flex-direction: column;
  }

  .dashboard-organisation-zero-state__actions {
    justify-content: flex-start;
  }
}
</style>
